import React, { useState } from 'react';
import { DynamicIcon } from '../icons/DynamicIcon';

interface Theme {
  nome: string;
  cores: {
    primaria: string;
    secundaria: string;
    fundo: string;
    superficie: string;
    textoPrincipal: string;
    textoSecundario: string;
  };
}

interface TabPersonalizacaoProps {
  eventoData: {
    tema: Theme;
    nomeEvento: string;
    dataEvento: string;
  };
  setEventoData: React.Dispatch<React.SetStateAction<any>>;
}

const TabPersonalizacao: React.FC<TabPersonalizacaoProps> = ({ eventoData, setEventoData }) => {
  const [mostrarAvancado, setMostrarAvancado] = useState(false);
  const [mostrarPrevia, setMostrarPrevia] = useState(false);

  const temas = [
    {
      id: 'lendario',
      nome: 'Lendário',
      descricao: 'Academia Lendária',
      icon: '♾️',
      cores: {
        primaria: '#BEB484',
        secundaria: '#BEB484',
        fundo: '#161616',
        superficie: '#242424',
        textoPrincipal: '#FFFFFF',
        textoSecundario: '#B8B8B8'
      }
    },
    {
      id: 'profissional',
      nome: 'Profissional',
      descricao: 'Confiança Corporativa',
      icon: '💼',
      cores: {
        primaria: '#3B82F6',
        secundaria: '#D97706',
        fundo: '#FFFFFF',
        superficie: '#F3F4F6',
        textoPrincipal: '#111827',
        textoSecundario: '#6B7280'
      }
    },
    {
      id: 'elegante',
      nome: 'Elegante',
      descricao: 'Sofisticação Premium',
      icon: '✨',
      cores: {
        primaria: '#4C566A',
        secundaria: '#5E81AC',
        fundo: '#FFFFFF',
        superficie: '#ECEFF4',
        textoPrincipal: '#2E3440',
        textoSecundario: '#4C566A'
      }
    },
    {
      id: 'natural',
      nome: 'Natural',
      descricao: 'Natureza & Sustentabilidade',
      icon: '🌿',
      cores: {
        primaria: '#22C55E',
        secundaria: '#A0522D',
        fundo: '#FFFFFF',
        superficie: '#F0FDF4',
        textoPrincipal: '#166534',
        textoSecundario: '#4B5563'
      }
    }
  ];

  const aplicarTema = (tema: any) => {
    setEventoData({
      ...eventoData,
      tema: {
        nome: tema.id,
        cores: tema.cores
      }
    });
  };

  const calcularContraste = (cor1: string, cor2: string) => {
    // Converter hex para RGB
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      } : null;
    };

    const rgb1 = hexToRgb(cor1);
    const rgb2 = hexToRgb(cor2);

    if (!rgb1 || !rgb2) return 0;

    // Calcular luminância relativa
    const luminancia = (rgb: {r: number, g: number, b: number}) => {
      const { r, g, b } = rgb;
      const [rs, gs, bs] = [r, g, b].map(c => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
      });
      return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
    };

    const l1 = luminancia(rgb1);
    const l2 = luminancia(rgb2);

    return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05);
  };

  const getContrasteBadge = (contraste: number) => {
    if (contraste >= 7) return { text: 'AAA', class: 'bg-green-600' };
    if (contraste >= 4.5) return { text: 'AA', class: 'bg-yellow-600' };
    return { text: 'Baixo', class: 'bg-red-600' };
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <h2 className="text-xl font-bold mb-2">Personalize as Cores do Orçamento</h2>
      <p className="text-gray-600 mb-6">Escolha as cores que representam sua marca no orçamento</p>

      {/* Temas Predefinidos */}
      <div className="mb-8">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <span className="text-xl">🎨</span>
          Temas Predefinidos
        </h3>
        <div className="grid md:grid-cols-2 gap-4">
          {temas.map(tema => (
            <div
              key={tema.id}
              onClick={() => aplicarTema(tema)}
              className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                eventoData.tema.nome === tema.id
                  ? 'border-purple-700 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <span className="text-3xl">{tema.icon}</span>
                <div>
                  <h4 className="font-semibold">{tema.nome}</h4>
                  <p className="text-sm text-gray-600">{tema.descricao}</p>
                </div>
              </div>
              <div className="flex gap-2">
                {Object.values(tema.cores).slice(0, 6).map((cor, idx) => (
                  <div
                    key={idx}
                    className="w-8 h-8 rounded"
                    style={{ backgroundColor: cor }}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Botões de Ação */}
      <div className="flex gap-4 flex-wrap">
        <button
          onClick={() => setMostrarAvancado(!mostrarAvancado)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded hover:bg-gray-200"
        >
          <DynamicIcon name="eye" size={16} />
          {mostrarAvancado ? 'Ocultar' : 'Mostrar'} Personalização Avançada
        </button>
        <button
          onClick={() => setMostrarPrevia(!mostrarPrevia)}
          className="flex items-center gap-2 px-4 py-2 bg-purple-100 text-purple-700 rounded hover:bg-purple-200"
        >
          <DynamicIcon name="eye" size={16} />
          {mostrarPrevia ? 'Ocultar' : 'Mostrar'} Prévia do Orçamento
        </button>
      </div>

      {/* Personalização Avançada */}
      {mostrarAvancado && (
        <div className="mt-6 p-6 bg-gray-900 text-white rounded-lg">
          <h3 className="text-xl font-bold mb-6 flex items-center gap-2">
            Personalização Avançada
            <span className="text-sm bg-gray-700 px-2 py-1 rounded">
              <DynamicIcon name="info" size={16} />
            </span>
          </h3>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Cores Principais */}
            <div>
              <h4 className="font-semibold mb-1">Cor Primária</h4>
              <div className="flex items-center gap-4 mb-2">
                <input
                  type="color"
                  value={eventoData.tema.cores.primaria}
                  onChange={(e) => setEventoData({
                    ...eventoData,
                    tema: {
                      ...eventoData.tema,
                      cores: { ...eventoData.tema.cores, primaria: e.target.value }
                    }
                  })}
                  className="w-20 h-20 rounded cursor-pointer"
                />
                <div>
                  <p className="font-mono text-lg">{eventoData.tema.cores.primaria}</p>
                  <p className="text-sm text-gray-400">Cor principal da marca, usada em destaques e botões</p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span>Contraste:</span>
                <span className="text-green-400">
                  {calcularContraste(eventoData.tema.cores.primaria, eventoData.tema.cores.fundo).toFixed(1)}:1
                </span>
                <span className={`px-2 py-1 rounded text-xs ${
                  getContrasteBadge(calcularContraste(eventoData.tema.cores.primaria, eventoData.tema.cores.fundo)).class
                }`}>
                  {getContrasteBadge(calcularContraste(eventoData.tema.cores.primaria, eventoData.tema.cores.fundo)).text}
                </span>
              </div>
            </div>

            {/* Cor Secundária */}
            <div>
              <h4 className="font-semibold mb-1">Cor Secundária</h4>
              <div className="flex items-center gap-4 mb-2">
                <input
                  type="color"
                  value={eventoData.tema.cores.secundaria}
                  onChange={(e) => setEventoData({
                    ...eventoData,
                    tema: {
                      ...eventoData.tema,
                      cores: { ...eventoData.tema.cores, secundaria: e.target.value }
                    }
                  })}
                  className="w-20 h-20 rounded cursor-pointer"
                />
                <div>
                  <p className="font-mono text-lg">{eventoData.tema.cores.secundaria}</p>
                  <p className="text-sm text-gray-400">Cor complementar para elementos secundários</p>
                </div>
              </div>
            </div>

            {/* Cor de Fundo */}
            <div>
              <h4 className="font-semibold mb-1">Cor de Fundo</h4>
              <div className="flex items-center gap-4 mb-2">
                <input
                  type="color"
                  value={eventoData.tema.cores.fundo}
                  onChange={(e) => setEventoData({
                    ...eventoData,
                    tema: {
                      ...eventoData.tema,
                      cores: { ...eventoData.tema.cores, fundo: e.target.value }
                    }
                  })}
                  className="w-20 h-20 rounded cursor-pointer"
                />
                <div>
                  <p className="font-mono text-lg">{eventoData.tema.cores.fundo}</p>
                  <p className="text-sm text-gray-400">Cor de fundo principal do documento</p>
                </div>
              </div>
            </div>

            {/* Cor de Superfície */}
            <div>
              <h4 className="font-semibold mb-1">Cor de Superfície</h4>
              <div className="flex items-center gap-4 mb-2">
                <input
                  type="color"
                  value={eventoData.tema.cores.superficie}
                  onChange={(e) => setEventoData({
                    ...eventoData,
                    tema: {
                      ...eventoData.tema,
                      cores: { ...eventoData.tema.cores, superficie: e.target.value }
                    }
                  })}
                  className="w-20 h-20 rounded cursor-pointer"
                />
                <div>
                  <p className="font-mono text-lg">{eventoData.tema.cores.superficie}</p>
                  <p className="text-sm text-gray-400">Cor para cartões e elementos elevados</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Prévia do Orçamento */}
      {mostrarPrevia && (
        <div className="mt-6 p-6 rounded-lg" style={{ backgroundColor: eventoData.tema.cores.fundo }}>
          <div className="max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold mb-4" style={{ color: eventoData.tema.cores.textoPrincipal }}>
              Orçamento de Evento
            </h3>
            <div className="p-4 rounded" style={{ backgroundColor: eventoData.tema.cores.superficie }}>
              <p style={{ color: eventoData.tema.cores.textoSecundario }}>
                Cliente: {eventoData.nomeEvento || 'Nome do Evento'}
              </p>
              <p style={{ color: eventoData.tema.cores.textoSecundario }}>
                Data: {eventoData.dataEvento ? new Date(eventoData.dataEvento).toLocaleDateString('pt-BR') : 'DD/MM/AAAA'}
              </p>
              <div className="mt-4 p-3 rounded" style={{ backgroundColor: eventoData.tema.cores.primaria }}>
                <p className="font-bold" style={{ color: eventoData.tema.cores.fundo }}>
                  Valor Total: R$ 0,00
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TabPersonalizacao;